import { convertTimeZone } from '@magicdoorinc/date';
import { useLocalization, toast, createTriggerResource } from '@magicdoorinc/ui';
import { useNavigate } from '@solidjs/router';
import { createMemo, createSignal } from 'solid-js';
import { createMagicDoorContext } from '~/contexts/utils';
import { AnnouncementRepository } from '~/repositories/announcementRepository';
import { onboardingRepository } from '~/repositories/onboardingRepository';
import { NotificationStatus } from '~/swagger/portal/Api';
import { isEmailContentEmpty } from '~/utils/validations';
import type { Model } from '~/pages/announcements/add-announcement/types';
import type { AnnouncementListQuery } from '~/repositories/announcementRepository';
import type {
  AnnouncementsDto,
  GetAnnouncementTargetsRequestDto,
  HydratedAnnouncementTargetsDto,
  NotificationScheduleDto,
  SendAnnouncementRequestDto,
  TargetType,
} from '~/swagger/portal/Api';

const repo = new AnnouncementRepository();

const extractAttachmentSessionIds = (attachments: Model['chatPreUploadedAttachments']): string[] | undefined => {
  return attachments?.filter((attachment) => attachment.uploadId).map((attachment) => attachment.uploadId!);
};

const excludeIds = (ids: string[] | undefined, excluded: string[] | undefined): string[] | undefined => {
  if (!ids) return ids;
  if (!excluded?.length) return ids;
  return ids.filter((id) => !excluded.includes(id));
};

const extractTargetIds = (
  review: HydratedAnnouncementTargetsDto,
  targetType: TargetType
): {
  ownerIds?: string[];
  vendorIds?: string[];
  tenantIds?: string[];
  leaseIds?: string[];
} => {
  const result: {
    ownerIds?: string[];
    vendorIds?: string[];
    tenantIds?: string[];
    leaseIds?: string[];
  } = {};

  switch (targetType) {
    case 'owner':
      result.ownerIds = review.owners?.map((owner) => owner.id) || [];
      break;
    case 'vendor':
      result.vendorIds = review.vendors?.map((vendor) => vendor.id) || [];
      break;
    case 'tenant':
      if (review.portfolioTargets && review.portfolioTargets.length > 0) {
        result.tenantIds = [];
        review.portfolioTargets.forEach((portfolio) => {
          portfolio.properties.forEach((property) => {
            property.units.forEach((unit) => {
              if (unit.tenants) {
                result.tenantIds!.push(...unit.tenants.map((tenant) => tenant.id));
              }
            });
          });
        });
      } else if (review.tenants) {
        result.tenantIds = review.tenants.map((tenant) => tenant.id);
      }
      break;
    case 'lease':
      result.leaseIds = [];
      review.portfolioTargets.forEach((portfolio) => {
        portfolio.properties.forEach((property) => {
          property.units.forEach((unit) => {
            if (unit.leases) {
              result.leaseIds!.push(...unit.leases.map((lease) => lease.id));
            }
          });
        });
      });
      break;
  }

  return result;
};

export const [AnnouncementProvider, useAnnouncements] = createMagicDoorContext('Announcements', () => {
  const navigate = useNavigate();
  const { t } = useLocalization();
  const [announcements, setAnnouncements] = createSignal<AnnouncementsDto[]>([]);
  const [isLoading, setIsLoading] = createSignal(false);
  const [error, setError] = createSignal<Error | null>(null);
  const [announcementReview, setAnnouncementReview] = createSignal<HydratedAnnouncementTargetsDto>();
  const [pendingScheduleByAnnouncement, setPendingScheduleByAnnouncement] = createSignal<Record<string, NotificationScheduleDto>>({});

  const steps = createMemo(() => [t('Select target'), t('Content'), t('Review')]);

  const [announcement, getAnnouncement] = createTriggerResource(repo.getAnnouncementDetails.bind(repo));

  const fetchAnnouncementTargets = async (filter?: GetAnnouncementTargetsRequestDto) => {
    setIsLoading(true);
    try {
      const response = await repo.getAnnouncementTargets(filter);
      return response;
    } catch (err) {
      setError(err instanceof Error ? err : new Error(String(err)));
      toast(t('Failed to fetch announcement targets'), 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const fetchAnnouncements = async (filter?: AnnouncementListQuery) => {
    setIsLoading(true);
    try {
      const response = await repo.getAnnouncements(filter);
      setAnnouncements(response);
    } catch (err) {
      setError(err instanceof Error ? err : new Error(String(err)));
      toast(t('Failed to fetch announcements'), 'error');
    } finally {
      setIsLoading(false);
    }
  };

  const fetchPendingSchedules = async () => {
    try {
      const response = await onboardingRepository.getPlannedNotifications({ status: NotificationStatus.Pending, pageSize: 100 });
      const map: Record<string, NotificationScheduleDto> = {};
      response.items.forEach((schedule) => {
        if (schedule.announcementId) {
          map[schedule.announcementId] = schedule;
        }
      });
      setPendingScheduleByAnnouncement(map);
    } catch (err) {
      setError(err instanceof Error ? err : new Error(String(err)));
    }
  };

  const cancelScheduledAnnouncement = async (announcementId: string) => {
    const schedule = pendingScheduleByAnnouncement()[announcementId];
    if (!schedule) return;

    try {
      await onboardingRepository.deleteNotificationSchedule(schedule.id);
      setPendingScheduleByAnnouncement((prev) => {
        const next = { ...prev };
        delete next[announcementId];
        return next;
      });
      toast.success(t('Scheduled announcement has been cancelled'));
    } catch (err) {
      setError(err instanceof Error ? err : new Error(String(err)));
      toast(t('Failed to cancel scheduled announcement'), 'error');
    }
  };

  const createAnnouncement = async (model: Model) => {
    const chatMessage = model.chatMessage?.trim() ? model.chatMessage : undefined;
    const emailMessage = model.emailMessage && !isEmailContentEmpty(model.emailMessage) ? model.emailMessage : undefined;

    const review = announcementReview();
    if (!review) {
      toast(t('Please review the announcement targets first'), 'error');
      return;
    }

    const targetIds = extractTargetIds(review, model.targetType as TargetType);

    targetIds.tenantIds = excludeIds(targetIds.tenantIds, model.excludedTenantIds);
    targetIds.ownerIds = excludeIds(targetIds.ownerIds, model.excludedOwnerIds);
    targetIds.vendorIds = excludeIds(targetIds.vendorIds, model.excludedVendorIds);
    targetIds.leaseIds = excludeIds(targetIds.leaseIds, model.excludedLeaseIds);

    if (model.targetType === 'tenant' && model.additionalTenantIds && model.additionalTenantIds.length > 0) {
      targetIds.tenantIds = [...new Set([...(targetIds.tenantIds ?? []), ...model.additionalTenantIds])];
    }

    const requestData: SendAnnouncementRequestDto & { sendText?: boolean; documentTemplateIds?: string[] } = {
      chatMessage,
      emailMessage,
      chatAttachmentSessionIds: extractAttachmentSessionIds(model.chatPreUploadedAttachments),
      emailAttachmentSessionIds: extractAttachmentSessionIds(model.emailPreUploadedAttachments),
      documentTemplateIds: model.documentTemplates?.length ? model.documentTemplates.map((tpl) => tpl.id) : undefined,
      subject: model.subject,
      sendText: model.sendText,
      scheduledSendAt: model.isScheduledNow
        ? undefined
        : model.scheduledSendAt
          ? convertTimeZone(model.scheduledSendAt).format('YYYY-MM-DDTHH:mm:ss')
          : undefined,
      ...targetIds,
    };

    const result = await repo.sendAnnouncementByIds(requestData as SendAnnouncementRequestDto);

    if (result.isFullFailure) {
      toast(t('Announcement failed to send to all {count} targets', { count: String(result.totalTargets) }), 'error');
    } else if (result.isPartialSuccess) {
      toast.error(
        t('Announcement sent to {success} of {total} targets. {failure} failed.', {
          success: String(result.successCount),
          total: String(result.totalTargets),
          failure: String(result.failureCount),
        })
      );
    } else {
      toast.success(t('Announcement has been sent successfully'));
    }

    if (result.id) {
      navigate(`/communications/announcements/${result.id}`, { replace: true });
    } else {
      navigate('/communications/announcements', { replace: true });
    }
  };

  const getAnnouncementTargets = async (currentModel: Model) => {
    const filter: Partial<GetAnnouncementTargetsRequestDto> = {
      targetType: currentModel.targetType as TargetType,
    };

    const targetType = currentModel.targetType as TargetType;

    switch (targetType) {
      case 'lease':
        if (currentModel.includeEndedLease !== undefined && currentModel.includeEndedLease !== null) {
          filter.includeEnded = currentModel.includeEndedLease;
        }
        if (currentModel.hasBalanceDue !== undefined && currentModel.hasBalanceDue !== null) {
          filter.hasBalanceDue = currentModel.hasBalanceDue;
        }
        break;

      case 'tenant':
        if (currentModel.includeEndedLease !== undefined && currentModel.includeEndedLease !== null) {
          filter.hasActiveLease = !currentModel.includeEndedLease;
        }
        if (currentModel.hasBalanceDue !== undefined && currentModel.hasBalanceDue !== null) {
          filter.hasBalanceDue = currentModel.hasBalanceDue;
        }
        break;

      case 'owner':
      case 'vendor':
        break;
    }

    if (currentModel.portfolioIds && currentModel.portfolioIds.length > 0) {
      filter.portfolioIds = currentModel.portfolioIds;
    }
    if (currentModel.propertyIds && currentModel.propertyIds.length > 0) {
      filter.propertyIds = currentModel.propertyIds;
    }
    if (currentModel.unitIds && currentModel.unitIds.length > 0) {
      filter.unitIds = currentModel.unitIds;
    }

    const response = await fetchAnnouncementTargets(filter as GetAnnouncementTargetsRequestDto);
    setAnnouncementReview(response);
  };

  return {
    announcements,
    isLoading,
    error,
    fetchAnnouncementTargets,
    fetchAnnouncements,
    fetchPendingSchedules,
    pendingScheduleByAnnouncement,
    cancelScheduledAnnouncement,
    getAnnouncement,
    announcement,
    createAnnouncement,
    getAnnouncementTargets,
    announcementReview,
    steps,
  };
});
